/**
 * Portal isolation utility to prevent cross-portal authentication sharing
 * This addresses the issue where AWS Amplify shares authentication state across tabs
 */

export class PortalIsolation {
  private static listeners: Set<() => void> = new Set()

  /**
   * Initialize portal isolation for a specific portal type
   */
  static initialize(portalType: 'admin' | 'finance') {
    // Add storage event listener to detect cross-portal logins
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key && this.isOtherPortalKey(event.key, portalType)) {
        // Another portal has signed in, clear this portal's state
        this.clearCurrentPortalState(portalType)

        // Force reload to login screen
        window.location.href = window.location.origin + window.location.pathname
      }
    }

    window.addEventListener('storage', handleStorageChange)

    // Store the cleanup function
    this.listeners.add(() => {
      window.removeEventListener('storage', handleStorageChange)
    })

    // Also listen for focus events to check for cross-portal contamination
    const handleFocus = () => {
      this.checkForCrossPortalContamination(portalType)
    }

    window.addEventListener('focus', handleFocus)
    this.listeners.add(() => {
      window.removeEventListener('focus', handleFocus)
    })

    // Check immediately on initialization
    this.checkForCrossPortalContamination(portalType)
  }

  /**
   * Check if a localStorage key belongs to another portal
   */
  private static isOtherPortalKey(key: string, currentPortal: 'admin' | 'finance'): boolean {
    const otherPortal = currentPortal === 'admin' ? 'finance' : 'admin'
    return key.includes(`${otherPortal}-auth-`)
  }

  /**
   * Clear the current portal's authentication state
   */
  private static clearCurrentPortalState(portalType: 'admin' | 'finance') {
    const keys = [
      `${portalType}-auth-tokens`,
      `${portalType}-auth-user`,
      `${portalType}-auth-store`
    ]

    keys.forEach(key => {
      localStorage.removeItem(key)
    })

    // Clear session storage
    sessionStorage.removeItem(`${portalType}-auth-initialized`)
    sessionStorage.removeItem(`${portalType}-portal-session`)
  }

  /**
   * Check for cross-portal contamination and clear if found
   */
  private static checkForCrossPortalContamination(portalType: 'admin' | 'finance') {
    const otherPortal = portalType === 'admin' ? 'finance' : 'admin'

    // Check if other portal has active tokens
    const otherPortalTokens = localStorage.getItem(`${otherPortal}-auth-tokens`)
    const otherPortalUser = localStorage.getItem(`${otherPortal}-auth-user`)

    if (otherPortalTokens || otherPortalUser) {
      // Other portal is active, clear current portal
      this.clearCurrentPortalState(portalType)

      // Force reload to ensure clean state
      if (window.location.pathname !== '/login' && !window.location.pathname.includes('login')) {
        window.location.href = window.location.origin + window.location.pathname
      }
    }
  }

  /**
   * Mark current portal as active
   */
  static markPortalActive(portalType: 'admin' | 'finance') {
    sessionStorage.setItem(`${portalType}-portal-session`, 'active')

    // Clear other portal's session markers
    const otherPortal = portalType === 'admin' ? 'finance' : 'admin'
    sessionStorage.removeItem(`${otherPortal}-portal-session`)
  }

  /**
   * Check if current portal is active
   */
  static isPortalActive(portalType: 'admin' | 'finance'): boolean {
    return sessionStorage.getItem(`${portalType}-portal-session`) === 'active'
  }

  /**
   * Cleanup all listeners
   */
  static cleanup() {
    this.listeners.forEach(cleanup => cleanup())
    this.listeners.clear()
  }
}